import { useCallback, useState } from 'react';

import useListFocusManager from '../../../components/lists/useListFocusManager';
import { getMatchingFormulas } from '../bridge/formulaPickerUtils';
import { hyperFormulaList } from './manager/hyperFormulaInstance';

export type FormulaPickerRef = {
    setFormulaPickerQuery: (query?: string) => boolean;
    hideFormulaPicker: () => void;
};

type Props = {
    addFormulaToCell: (formula: string, replacePrevCharacters: number) => void;
    show: boolean;
    setShow: (show: boolean) => void;
    replaceFormulaQueryOnInsert: boolean;
};

/**
 * Controls formula picker for both desktop popup and mobile sheet renderers.
 */
const useFormulaPickerController = ({ show, setShow, addFormulaToCell, replaceFormulaQueryOnInsert }: Props) => {
    const [formulaPickerData, setFormulaPickerData] = useState<string[]>(hyperFormulaList);
    const [formulaQuery, setFormulaQuery] = useState('');

    const listFocusManager = useListFocusManager({
        loop: true,
        initialFocusIndex: 0,
        listSize: formulaPickerData.length,
    });
    const { focusedIndex, setFocusIndex } = listFocusManager;

    const showFormulaPicker = show && formulaPickerData.length > 0;

    const setFormulaPickerQuery = useCallback(
        (query = ''): boolean => {
            setShow(true);
            setFocusIndex(0);
            setFormulaQuery(query);

            const newFormulaList = getMatchingFormulas(query, hyperFormulaList);
            setFormulaPickerData(newFormulaList);

            return newFormulaList.length > 0;
        },
        [setFocusIndex, setShow],
    );

    const hideFormulaPicker = useCallback(() => {
        setShow(false);
        setFormulaQuery('');
        setFormulaPickerData([]);
    }, [setShow]);

    const selectFormula = useCallback(
        (formula: string) => {
            const replacePrevCharacters = replaceFormulaQueryOnInsert ? formulaQuery.length : 0;

            addFormulaToCell(formula, replacePrevCharacters);
            hideFormulaPicker();
        },
        [addFormulaToCell, formulaQuery, hideFormulaPicker, replaceFormulaQueryOnInsert],
    );

    return {
        focusedIndex,
        formulaPickerData,
        formulaQuery,
        hideFormulaPicker,
        listFocusManager,
        selectFormula,
        setFormulaPickerQuery,
        showFormulaPicker,
    };
};

export default useFormulaPickerController;
